/**
 * Watchtower Calendar Export — types
 *
 * See docs/architecture/watchtower-calendar-export.md §3 for the locked
 * generator contract. This file is the single source of truth for the
 * `CalendarExportOptions` / `CalendarExportResult` shapes consumed by the
 * generator and (in later PRs) the export modal + audit endpoint.
 */

import type { DeadlinePriority } from '../../components/deadlines/types';

/**
 * Options the caller passes to `buildDeadlineIcs`. The single PHI toggle
 * (`includeRecipientName`) controls whether `CaseFile.name` (the care
 * recipient's full name per the v2.4 schema) appears in the .ics output.
 *
 * `customTitle` and `customDescription` override the PHI-safe defaults when
 * defined. Trimmed-equal-to-default counts as "not modified" — see
 * `CalendarExportResult.diff` for the analytics contract.
 */
export interface CalendarExportOptions {
  /** Single PHI toggle. Default: false. */
  includeRecipientName: boolean;
  /** Override SUMMARY. Trimmed equality with default → not flagged as modified. */
  customTitle?: string;
  /** Override DESCRIPTION. Trimmed equality with default → not flagged as modified. */
  customDescription?: string;
}

/**
 * Result of `buildDeadlineIcs`. `ics` is the RFC 5545 text body; `filename`
 * is always opaque (`hesvara-deadline-{shortId}.ics`); `diff` is consumed by
 * the audit endpoint in PR-2 to record categorical modification flags
 * without recording the literal text.
 */
export interface CalendarExportResult {
  ics: string;
  filename: string;
  diff: {
    titleModified: boolean;
    descriptionModified: boolean;
    includedRecipientName: boolean;
  };
}

/**
 * iCal `PRIORITY` is 0-9 (0=undefined, 1=highest, 9=lowest). The mapping is
 * locked by the architecture doc §3 priority table.
 */
export const ICS_PRIORITY_MAP: Record<DeadlinePriority, number> = {
  critical: 1,
  high: 3,
  medium: 5,
  low: 7,
};

/**
 * Reference epoch used to compute monotonic SEQUENCE values that fit
 * comfortably in a signed 32-bit int. 2025-01-01T00:00:00Z. Locked by
 * architecture doc §3 ("SEQUENCE (resolved)") and §10 risks #3.
 */
export const HESVARA_EPOCH = 1735689600;

/** Locked PRODID per architecture doc §3 + Appendix A. */
export const HESVARA_PRODID = '-//Hesvara//Watchtower 1.0//EN';

/** UID domain per architecture doc §0 decision #8 (deterministic UID). */
export const HESVARA_UID_DOMAIN = 'hesvara.com';
